import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { DsTooltipPosition, Icons, IconStyles } from '@levelaccess/design-system';
import { INavMenuItem, NavigationItem } from '../../../services/navigation/nav-structure';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { CommonUtility } from '../../../utility/common.utility';
import { RoutingService } from '../../../services/routing.service';

@Component({
  selector: 'app-vertical-menu',
  templateUrl: './vertical-menu.component.html',
  styleUrls: ['./vertical-menu.component.scss'],
  standalone: false,
})
export class VerticalMenuComponent implements OnChanges {
  private focusedMenuItem: INavMenuItem = null;
  private expandedSubmenus: Map<NavigationItem, boolean> = new Map<NavigationItem, boolean>();

  @ViewChildren('submenuButton') private submenuButtons: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChildren('submenuPanel') private submenuPanels: QueryList<ElementRef<HTMLDivElement>>;

  public uniqueId: string = CommonUtility.createUniqueDOMId();
  public DsTooltipPosition: typeof DsTooltipPosition = DsTooltipPosition;
  public IconStyles: typeof IconStyles = IconStyles;
  public Icons: typeof Icons = Icons;

  @Input({ required: true }) public isCollapsed: boolean;
  @Input({ required: true }) public menuTitle: string;
  @Input({ required: true }) public menuItems: INavMenuItem[];
  @Output() public menuItemClicked: EventEmitter<INavMenuItem> = new EventEmitter<INavMenuItem>();

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private routingService: RoutingService,
  ) {}

  public isSubmenuExpanded(parentMenuId: NavigationItem): boolean {
    return this.expandedSubmenus.has(parentMenuId);
  }

  public isMenuItemActive(menuItem: INavMenuItem): boolean {
    if (SharedCommonUtility.notNullishOrEmpty(menuItem.children)) {
      return this.isCollapsed ? menuItem.children.some((item: INavMenuItem) => item.isActive) : false;
    }

    return menuItem.isActive;
  }

  protected getAriaLabelKey(menuItem: INavMenuItem): string {
    return menuItem.ariaLabel ?? menuItem.label;
  }

  public handleSubMenuFocusin(parentMenuItem: INavMenuItem): void {
    this.focusedMenuItem = parentMenuItem;
  }

  public handleSubMenuFocusout(event: FocusEvent, parentMenuItem: INavMenuItem): void {
    if (
      event.target instanceof HTMLElement &&
      event.relatedTarget instanceof HTMLElement &&
      event.target.closest('li.submenu-container').contains(event.relatedTarget)
    ) {
      return;
    }

    this.focusedMenuItem = null;
    this.closeCollapsedStateSubmenu(parentMenuItem.id);
  }

  public handleSubMenuMouseenter(parentMenuItem: INavMenuItem): void {
    this.openCollapsedStateSubmenu(parentMenuItem.id);
  }

  public handleSubMenuMouseleave(parentMenuItem: INavMenuItem): void {
    if (parentMenuItem.id === this.focusedMenuItem?.id) {
      return;
    }
    this.closeCollapsedStateSubmenu(parentMenuItem.id);
  }

  public toggleExpandedStateSubmenu(parentMenuId: NavigationItem): void {
    if (this.isSubmenuExpanded(parentMenuId)) {
      this.closeSubmenu(parentMenuId);
    } else {
      this.openSubmenu(parentMenuId);
    }
  }

  private isExternalItem(menuItem: INavMenuItem): boolean {
    return menuItem.external || SharedCommonUtility.notNullish(menuItem.externalLink);
  }

  public onMenuItemClick(menuItem: INavMenuItem): void {
    if (!this.isExternalItem(menuItem)) {
      this.routingService.requestMoveFocusOnNavigationEnd();
    }
    if (menuItem.onClick) {
      menuItem.onClick();
    }
    this.menuItemClicked.emit(menuItem);
  }

  private openCollapsedStateSubmenu(parentMenuId: NavigationItem): void {
    if (!this.isCollapsed) {
      return;
    }

    this.openSubmenu(parentMenuId);
  }

  private closeCollapsedStateSubmenu(parentMenuId: NavigationItem): void {
    if (!this.isCollapsed) {
      return;
    }

    this.closeSubmenu(parentMenuId);
  }

  private openSubmenu(parentMenuId: NavigationItem): void {
    if (this.expandedSubmenus.has(parentMenuId)) {
      return;
    }

    const submenuButton: HTMLButtonElement = this.getSubmenuButton(parentMenuId);
    const submenuPanel: HTMLDivElement = this.getSubmenuPanel(parentMenuId);
    if (SharedCommonUtility.notNullish(submenuButton) && SharedCommonUtility.notNullish(submenuPanel)) {
      submenuPanel.classList.add('expanded');
      this.expandedSubmenus.set(parentMenuId, true);

      if (this.isCollapsed) {
        this.setSubmenuPosition(submenuButton, submenuPanel);
      }
    }
  }

  private setSubmenuPosition(submenuButton: HTMLButtonElement, submenuPanel: HTMLDivElement): void {
    const submenuButtonDimensions: DOMRect = submenuButton.getBoundingClientRect();
    const submenuPanelDimensions: DOMRect = submenuPanel.getBoundingClientRect();
    if (submenuButtonDimensions.bottom + submenuPanelDimensions.height > window.innerHeight) {
      submenuPanel.style.top = submenuButtonDimensions.bottom - submenuPanelDimensions.height + 'px';
    } else {
      submenuPanel.style.top = submenuButtonDimensions.top + 'px';
    }
  }

  private closeSubmenu(parentMenuId: NavigationItem): void {
    if (this.expandedSubmenus.has(parentMenuId)) {
      const submenuPanel: HTMLDivElement = this.getSubmenuPanel(parentMenuId);
      submenuPanel.classList.remove('expanded');
      this.expandedSubmenus.delete(parentMenuId);
    }
  }

  private getSubmenuButton(parentMenuId: NavigationItem): HTMLButtonElement {
    return this.submenuButtons?.find(
      (button: ElementRef<HTMLButtonElement>) => button.nativeElement.id === `${parentMenuId}-button`,
    )?.nativeElement;
  }

  private getSubmenuPanel(parentMenuId: NavigationItem): HTMLDivElement {
    return this.submenuPanels?.find((panel: ElementRef<HTMLDivElement>) => panel.nativeElement.id === `${parentMenuId}-submenu`)
      ?.nativeElement;
  }

  private getMenuItemElements(): HTMLElement[] {
    const menuItemSelectors: string[] = this.menuItems.reduce((selectors: string[], menuItem: INavMenuItem): string[] => {
      const toSelector = (item: INavMenuItem): string => `[data-item-id="${item.id}"]`;
      const childSelectors: string[] = this.expandedSubmenus.has(menuItem.id) ? menuItem.children.map(toSelector) : [];
      selectors.push(toSelector(menuItem), ...childSelectors);
      return selectors;
    }, []);
    return Array.from(this.elementRef.nativeElement.querySelectorAll(menuItemSelectors.join(',')));
  }

  public initializeExpandedSubmenus(): void {
    this.expandedSubmenus.clear();

    if (this.isCollapsed) {
      return;
    }

    this.menuItems.forEach((menuItem: INavMenuItem) => {
      const hasActiveChild: boolean = menuItem.children?.some((item: INavMenuItem) => item.isActive);
      if (hasActiveChild) {
        this.expandedSubmenus.set(menuItem.id, true);
      }
    });
  }

  @HostListener('keydown.arrowUp', ['$event'])
  @HostListener('keydown.arrowDown', ['$event'])
  public handleArrowKeys(event: KeyboardEvent): void {
    event.preventDefault();

    if (document.activeElement instanceof HTMLElement) {
      const menuItemElements: HTMLElement[] = this.getMenuItemElements();
      const activeElementIndex: number = menuItemElements.indexOf(document.activeElement);
      const nextElementIndex: number = event.key === 'ArrowUp' ? activeElementIndex - 1 : activeElementIndex + 1;

      if (SharedCommonUtility.notNullish(menuItemElements[nextElementIndex])) {
        CommonUtility.setFocusToElement(null, menuItemElements[nextElementIndex]);
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscapeKey(event: KeyboardEvent): void {
    if (!this.isCollapsed || this.expandedSubmenus.size === 0) {
      return;
    }

    event.stopPropagation();

    // Only the submenu that actually has focus should get it back - closing a submenu that
    // was merely hover-opened (with focus elsewhere on the page) must not steal focus.
    const focusedSubmenuId: NavigationItem = this.focusedMenuItem?.id;
    for (const parentMenuId of Array.from(this.expandedSubmenus.keys())) {
      this.closeSubmenu(parentMenuId);
    }

    if (SharedCommonUtility.notNullish(focusedSubmenuId)) {
      CommonUtility.setFocusToElement(null, this.getSubmenuButton(focusedSubmenuId));
    }
  }

  public ngOnChanges(): void {
    this.initializeExpandedSubmenus();
  }
}
